/**
 * 用户信息修改表单组件
 * 用于修改当前用户的个人信息
 */

import { Descriptions, Form, Input, Modal, message, Select, Tag } from 'antd';
import React, { useEffect } from 'react';
import {
  getPlatV1AccessProfile,
  postPlatV1AccessUpdateProfile,
} from '@/services/swagger/pingtaijiekou';

interface UserProfileFormProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const UserProfileForm: React.FC<UserProfileFormProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [userInfo, setUserInfo] = React.useState<API.AccessProfileRes>({});

  // 获取当前用户信息
  useEffect(() => {
    if (visible) {
      loadUserInfo();
    }
  }, [visible]);

  const loadUserInfo = async () => {
    try {
      setLoading(true);
      const response = await getPlatV1AccessProfile({});

      if (response) {
        // 保存完整的用户信息
        setUserInfo(response);

        // AccessProfileRes 包含完整的用户信息字段
        form.setFieldsValue({
          nickname: response.nickname,
          phone: response.phone,
          email: response.email,
          gender: response.gender,
          qq: response.qq,
          remark: response.remark,
        } as any);
      }
    } catch (error: any) {
      console.error('获取用户信息失败:', error);
      message.error(error?.message || '获取用户信息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      // 构建更新参数 - 直接使用表单值
      await postPlatV1AccessUpdateProfile({
        nickname: values.nickname,
        phone: values.phone,
        email: values.email,
        gender: values.gender,
        qq: values.qq,
        remark: values.remark,
      });

      message.success('用户信息更新成功');
      onSuccess();
    } catch (error: any) {
      console.error('更新用户信息失败:', error);
      message.error(error?.message || '更新用户信息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="修改用户信息"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="保存"
      cancelText="取消"
    >
      {/* ✅ 规则要求：禁止显示技术标识符 - userId 不显示 */}
      <Descriptions
        bordered
        column={2}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label="用户名">
          {userInfo.username || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="状态">
          {userInfo.enabled === '1' ? (
            <Tag color="success">启用</Tag>
          ) : (
            <Tag color="default">禁用</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="所属组织">
          {userInfo.orgName || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="所属部门">
          {userInfo.deptName || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="岗位">
          {userInfo.postName || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="角色">
          {userInfo.roleName || '-'}
        </Descriptions.Item>
      </Descriptions>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          gender: '1',
        }}
      >
        <Form.Item
          label="昵称"
          name="nickname"
          rules={[{ required: true, message: '请输入昵称' }]}
        >
          <Input placeholder="请输入昵称" />
        </Form.Item>

        <Form.Item
          label="手机号"
          name="phone"
          rules={[{ pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }]}
        >
          <Input placeholder="请输入手机号" maxLength={11} />
        </Form.Item>

        <Form.Item
          label="邮箱"
          name="email"
          rules={[
            {
              type: 'email',
              message: '请输入正确的邮箱格式',
            },
          ]}
        >
          <Input placeholder="请输入邮箱" />
        </Form.Item>

        <Form.Item
          label="性别"
          name="gender"
          rules={[
            {
              required: true,
              message: '请选择性别',
            },
            {
              validator: (_, value) => {
                if (!value || ['0', '1', '2'].includes(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('性别必须是 0、1 或 2'));
              },
            },
          ]}
        >
          <Select placeholder="请选择性别" allowClear>
            <Select.Option value="0">未知</Select.Option>
            <Select.Option value="1">男</Select.Option>
            <Select.Option value="2">女</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item label="QQ" name="qq">
          <Input placeholder="请输入 QQ 号" />
        </Form.Item>

        <Form.Item label="备注" name="remark">
          <Input.TextArea rows={3} placeholder="请输入备注信息" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UserProfileForm;
